// Client access requests inbox — Calyvra Administrators only
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { KeyRound, Check, X, Loader2, Inbox, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import portalApi from '../lib/api';
import { toast } from 'sonner';

function fmt(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

const STATUS_TONE = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  denied: 'bg-red-100 text-red-700',
};

export default function AccessRequestsPage() {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [status, setStatus] = useState('pending');
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await portalApi.getAccessRequests(status);
      setRequests(data.requests || []);
      setPendingCount(data.pending_count || 0);
    } catch (err) {
      toast.error(err.message || 'Could not load requests');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, approve) => {
    setActing(id);
    try {
      if (approve) await portalApi.approveAccessRequest(id);
      else await portalApi.denyAccessRequest(id);
      toast.success(approve ? 'Access granted' : 'Request declined');
      load();
    } catch (err) {
      toast.error(err.message || 'Action failed');
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="max-w-4xl" data-testid="access-requests-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <KeyRound className="h-6 w-6 text-sky-600" /> Access Requests
            {pendingCount > 0 && <Badge className="bg-amber-100 text-amber-700" data-testid="pending-count-badge">{pendingCount} pending</Badge>}
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Agents requesting access to clients not assigned to them.</p>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[160px]" data-testid="request-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="denied">Denied</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>
        ) : requests.length === 0 ? (
          <div className="text-center py-16" data-testid="no-requests">
            <Inbox className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No {status !== 'all' ? status : ''} requests.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="requests-list">
            {requests.map((r) => (
              <li key={r.id} className="p-5 flex items-start justify-between gap-4 flex-wrap" data-testid={`request-row-${r.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/portal/clients/${r.client_id}`} className="font-semibold text-sky-600 hover:underline">{r.client_name}</Link>
                    {r.calyvra_client_id && <span className="text-xs text-slate-400 font-mono">{r.calyvra_client_id}</span>}
                    <Badge className={STATUS_TONE[r.status] || 'bg-slate-100 text-slate-600'}>{r.status}</Badge>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">
                    Requested by <strong>{r.requester_name}</strong> ({r.requester_email})
                    {r.workspace_name ? ` · ${r.workspace_name}` : ''}
                  </p>
                  {r.reason && <p className="text-sm text-slate-500 mt-1 italic">"{r.reason}"</p>}
                  <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Clock className="h-3 w-3" /> {fmt(r.created_at)}
                    {r.resolved_by_name ? ` · resolved by ${r.resolved_by_name}` : ''}</p>
                </div>
                {r.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => act(r.id, true)} disabled={acting === r.id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-btn-${r.id}`}>
                      {acting === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Grant
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => act(r.id, false)} disabled={acting === r.id}
                      className="border-red-200 text-red-600 hover:bg-red-50" data-testid={`deny-btn-${r.id}`}>
                      <X className="h-4 w-4" /> Decline
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
